import { ISelectType } from "@4uhub/lib4uhub";
import React, { createContext, useCallback, useContext, useState } from "react";
import { FieldValues } from "react-hook-form";
import { IStep } from "../../../pages/Triage/NewTriage/Stepper/models";
import { TAppointment } from "../../../pages/Triage/NewTriage/schema";

interface ITriageContext<T extends FieldValues = FieldValues> {
  activeStep: number;
  steps: IStep<T>[];
  setStepsList: (steps: IStep<T>[]) => void;
  patient?: ISelectType;
  insurance?: ISelectType;
  onActiveStep: (value: number | ((prevActiveStep: number) => number)) => void;
  onChangePatient: (value: ISelectType) => void;
  onChangeInsurance: (value: ISelectType) => void;
}

interface ITriageContextProviderProps {
  children: JSX.Element;
}

const TriageContext = createContext<ITriageContext<TAppointment>>({
  steps: [],
  activeStep: 0,
  setStepsList: () => {},
  patient: undefined,
  insurance: undefined,
  onChangePatient: () => {},
  onActiveStep: () => {},
  onChangeInsurance: () => {},
});

export const TriageContextProvider: React.FC<ITriageContextProviderProps> = ({
  children,
}) => {
  const [steps, setSteps] = useState<IStep<TAppointment>[]>([]);
  const [patient, setPatient] = useState<ISelectType | undefined>();
  const [insurance, setInsurance] = useState<ISelectType | undefined>();

  const [activeStep, setActiveStep] = React.useState(0);

  const setStepsList = useCallback((newSteps: IStep<TAppointment>[]) => {
    setSteps(newSteps);
    setActiveStep(0);
  }, []);

  const onChangePatient = useCallback((value: ISelectType) => {
    setPatient(value);
  }, []);

  const onActiveStep = useCallback(
    (value: number | ((prev: number) => number)) => {
      setActiveStep(value);
    },
    []
  );

  const onChangeInsurance = useCallback((value: ISelectType) => {
    setInsurance(value);
  }, []);

  return (
    <TriageContext.Provider
      value={{
        steps,
        patient,
        activeStep,
        insurance,
        onChangePatient,
        onChangeInsurance,
        setStepsList,
        onActiveStep,
      }}
    >
      {children}
    </TriageContext.Provider>
  );
};

export const useTriageContext = () => {
  const context = useContext(TriageContext);

  return context;
};
